import { TranslateService } from '@ngx-translate/core';
import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  Renderer2,
  HostListener,
  TemplateRef
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { SafeResourceUrl } from '@angular/platform-browser';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { MessageService } from 'primeng/api';
import { WebcamImage } from 'ngx-webcam';
import { PDFDocument } from 'pdf-lib';
import * as bootstrap from 'bootstrap';
import { environment } from 'src/environments/environment';
import { getSignatureBoxSizePx } from 'src/app/shared-components/signature-box.util';
import { UrlValidatorService } from 'src/app/services/url-validator.service';
import { ClientDetailsService } from 'src/app/services/client-details.service';
import { ESignService } from 'src/app/services/e-sign.service';
import { DomainType } from 'src/app/enum/domain-type.enum';
import { DocViewerState, LanguageOption } from './ai-features.interfaces';
import { AiFeaturesService } from 'src/app/services/ai-feature.service';

// Child component references
import { ElectronicSignComponent } from './electronic-sign/electronic-sign.component';
import { AadhaarSignComponent } from './aadhaar-sign/aadhaar-sign.component';

interface Coordinates {
  x1: number;
  x2: number;
  y1: number;
  y2: number;
}
interface PositionDetails {
  [key: string]: Coordinates[];
}

@Component({
    selector: 'app-second-version-esign',
    templateUrl: './second-version-esign.component.html',
    styleUrls: ['./second-version-esign.component.scss'],
    standalone: false
})
export class SecondVersionEsignComponent implements OnInit {
  // ─── Child component references ────────────────────────────────────────────
  /** Reference to the electronic-sign child, used to call getParallelSignData() / SubmitSign() */
  @ViewChild('electronicSign') electronicSignRef: ElectronicSignComponent;
  /** Reference to the aadhaar-sign child */
  @ViewChild('aadhaarSign') aadhaarSignRef: AadhaarSignComponent;

  // ─── Modal / template refs ─────────────────────────────────────────────────
  @ViewChild('otpScreen') otpScreen: TemplateRef<any>;
  @ViewChild('userImage') userImage: TemplateRef<any>;
  @ViewChild('geoFencingModal') geoFencingModal: TemplateRef<any>;
  @ViewChild('signPadCanvas', { static: false })
  signaturePadElement: ElementRef;
  @ViewChild('ngOtpInput', { static: false }) ngOtpInput: any;

  // ─── Routing flags ─────────────────────────────────────────────────────────
  /**
   * showEmudhra = true  → render <app-aadhaar-sign>
   * showEmudhra = false → render <app-electronic-sign>
   *
   * Set inside getSignerDetails() based on whether emudhra_gateway is present.
   */
  showEmudhra: boolean = false;

  // ─── AI / tab state ────────────────────────────────────────────────────────
  activeTab: string = 'esignature';
  selectedLanguage: string = '';
  supportedLanguages: LanguageOption[] = [];
  isLoadingLanguages: boolean = false;
  languagesLoaded: boolean = false;
  isTranslating: boolean = false;
  translatePdfSrc: SafeResourceUrl | null = null;
  isSummarizing: boolean = false;
  summaryContent: string | null = null;
  summaryError: string | null = null;
  compareFile: File | null = null;
  compareDropError: boolean = false;
  translatedHtml: string | null = null;
  showLanguageError: boolean = false;
  showFeaturesDropdown = false;
  featureOptions = [{ label: 'Summarize', value: 'summarize' }];
  selectedFeature: string | null = null;

  // ─── Zoom / navigation ─────────────────────────────────────────────────────
  zoomLevel: number = 100;
  readonly ZOOM_STEP = 10;
  readonly ZOOM_MIN = 50;
  readonly ZOOM_MAX = 200;
  pageJumpValue: number = 1;

  // ─── Layout ────────────────────────────────────────────────────────────────
  showSign: boolean = true;
  showFinish: boolean = true;

  // ─── Shared state (passed to children via @Input) ──────────────────────────
  isChecked: boolean = false;
  selectedConsentLang: string = 'en';
  consentLanguages = [
    { code: 'en', label: 'English' },
    { code: 'hi', label: 'हिन्दी' },
    { code: 'bn', label: 'বাংলা' },
    { code: 'te', label: 'తెలుగు' },
    { code: 'mr', label: 'मराठी' },
    { code: 'ta', label: 'தமிழ்' },
    { code: 'gu', label: 'ગુજરાતી' },
    { code: 'kn', label: 'ಕನ್ನಡ' },
    { code: 'or', label: 'ଓଡ଼ିଆ' },
    { code: 'ml', label: 'മലയാളം' },
    { code: 'pa', label: 'ਪੰਜਾਬੀ' },
    { code: 'as', label: 'অসমীয়া' },
  ];
  orderId: string;
  pdfUrl: SafeResourceUrl;
  signatoryDetails: any = {};
  queryParams: any;
  signerData: any = [];
  pageCheck: any;
  positionDetails: any = {};
  pageVariable = 1;
  numPdfPages: any;
  pageNo: number = 1;
  otherSignerDetails: any = [];
  beforeSigners: any = [];
  afterSigners: any = [];
  esignId: string;
  signatoryId: string;
  zeroSigners: any = [];
  pageHeight: number;
  isDocumentSigned: boolean = false;
  public webcamImage: any = null;
  showWebcam: boolean;
  signPosition: string = '';
  signPages: number[];
  base64Data: string;
  orderType: any;
  userData: any;
  completedSigners: any = [];
  pdfsizes: any = [];
  enableCaptureImage: boolean = true;

  // ─── Shared sign image (received from electronic-sign child via @Output) ───
  signImage: any = null;

  // ─── OTP state ─────────────────────────────────────────────────────────────
  interval: any;
  selectedUserEmail: any;
  showOtpMethod: boolean = true;
  pageLoaded: boolean = true;
  timeLeft: number = 60;
  otp: string;
  enableOtp: boolean = true;
  showOtpComponent: boolean = true;
  sendOtpLink: boolean = false;
  config = {
    allowNumbersOnly: true,
    length: 6,
    isPasswordInput: false,
    disableAutoFocus: false,
    placeholder: ''
  };

  // ─── Geo-fencing ───────────────────────────────────────────────────────────
  geoFencingData: any;
  isGeoFencingAllowed: boolean = true;

  // ─── Consent ───────────────────────────────────────────────────────────────
  displayConsentModal = false;

  // ─── Email / print / download ──────────────────────────────────────────────
  isEmailButtonDisabled: boolean = false;
  emailCooldownTime: number = 0;
  private emailCooldownInterval: any;

  // ─── Internal document base64 ──────────────────────────────────────────────
  private documentBase64: string = '';
  private isClosingModal: boolean = false;
  private esignPdfUrl: SafeResourceUrl | null = null;
  private redirecturl: string = '';
  isSignFailed: boolean = false;

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    this.showSign = window.innerWidth > 740;
  }

  constructor(
    private spinner: NgxSpinnerService,
    private modalService: NgbModal,
    private activeRouter: ActivatedRoute,
    private eSignService: ESignService,
    private renderer: Renderer2,
    private messageService: MessageService,
    private clientDetailsService: ClientDetailsService,
    private router: Router,
    private urlValidator: UrlValidatorService,
    private aiFeaturesService: AiFeaturesService,
    public translate: TranslateService
  ) {
    this.translate.setDefaultLang('en');
    this.translate.use('en');
    this.activeRouter.queryParams.subscribe((queryParams) => {
      this.queryParams = queryParams;
      this.esignId = this.queryParams.esign_id;
      this.signatoryId = this.queryParams.signatory_id;
    });
    this.showSign = window.innerWidth > 740;
  }

  ngOnInit(): void {
    window.scrollTo(0, 0);
    this.spinner.hide();
    this.renderer.setStyle(document.body, 'overflow-y', 'hidden');
    this.getSignerDetails();
    this.getClientData();
    this.startTimer();
  }

  ngOnDestroy(): void {
    this.redirecturl = '';
    if (this.emailCooldownInterval) {
      clearInterval(this.emailCooldownInterval);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.features-dropdown')) {
      this.showFeaturesDropdown = false;
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // SIGNER DETAILS
  // ──────────────────────────────────────────────────────────────────────────

  getSignerDetails(): void {
    this.eSignService.getSignerDetails(this.queryParams).subscribe((res) => {
      if (res['message'] === 'Signatory not found') {
        this.signerData = { signatory_details: { status: 'EXPIRED' } };
        this.signatoryDetails = this.signerData.signatory_details;
        this.isDocumentSigned = true;
        this.spinner.hide();
        return;
      }

      this.signerData = res['content'];
      this.documentBase64 = res['content'].document;
      this.pdfUrl = `data:application/pdf;base64,${this.documentBase64}`;
      this.esignPdfUrl = this.pdfUrl;
      this.translatePdfSrc = this.pdfUrl;
      this.orderType = this.signerData.order_type;
      this.signatoryDetails = this.signerData.signatory_details;
      this.orderId = this.signerData.order_id;
      this.selectedUserEmail = this.signatoryDetails.email;
      this.positionDetails = this.signatoryDetails.position_details || {};
      this.signPosition = this.signatoryDetails.sign_position;
      this.enableCaptureImage = this.signatoryDetails.esign_webcam;

      // ── Determine which child to show ─────────────────────────────────────
      const emudhraParms = this.signatoryDetails.emudhra_gateway;
      this.showEmudhra = !!(
        emudhraParms && Object.keys(emudhraParms).length > 0
      );

      if (this.signatoryDetails.status === 'FAILED') {
        this.isSignFailed = true;
        this.router.navigate(['signFailed']);
        return;
      }

      if (
        ['COMPLETED', 'CANCELLED', 'EXPIRED'].includes(
          this.signatoryDetails.status
        )
      ) {
        this.isDocumentSigned = true;
      }

      this.otherSignerDetails = this.signerData.other_signatories;
      this.getSequenceOrder();

      if (this.orderType === 'CAT_EDIT') {
        this.getReSizeDocument(res['content'].document);
      }

      if (!this.isDocumentSigned) {
        if (this.showOtpMethod && this.signatoryDetails.esign_otp) {
          this.onOtpMethod();
          this.showOtpMethod = false;
          // Consent will open after OTP is verified (in otpOnSubmit)
        } else {
          this.displayConsentModal = true;
        }
      }

      if (this.orderId) {
        this.getIpGeofencing();
      }
    });
    this.spinner.hide();
  }

  // ──────────────────────────────────────────────────────────────────────────
  // CHILD OUTPUT HANDLERS
  // ──────────────────────────────────────────────────────────────────────────

  /** Called when electronic-sign child emits signCompleted */
  onElectronicSignCompleted(): void {
    this.getSignerDetails();
    if (this.redirecturl) {
      this.spinner.hide();
      this.messageService.add({
        severity: 'success',
        detail: 'Process completed. Redirecting to sign...'
      });
      setTimeout(() => {
        if (
          this.urlValidator.isValidRedirectUrl(
            this.redirecturl.toString(),
            DomainType.DOQFY
          )
        ) {
          window.location.href = this.redirecturl;
        }
      }, 1500);
    } else {
      this.spinner.hide();
      this.messageService.add({
        severity: 'success',
        detail: 'Process completed successfully.'
      });
    }
  }

  /** Called when electronic-sign child emits signImageChange */
  onSignImageChange(image: any): void {
    this.signImage = image;
  }

  /** Called when aadhaar-sign child emits signCompleted */
  onAadhaarSignCompleted(): void {
    this.getSignerDetails();
  }

  // ──────────────────────────────────────────────────────────────────────────
  // FORWARDING CALLS TO CHILDREN  (from nav buttons / right-panel buttons)
  // ──────────────────────────────────────────────────────────────────────────

  /** "Set Signature" / "Sign Now" — forwards to electronic-sign child */
  triggerElectronicSign(): void {
    this.electronicSignRef?.getParallelSignData();
  }

  /** "Finish & Submit" — forwards to electronic-sign child */
  triggerSubmitSign(): void {
    this.electronicSignRef?.SubmitSign();
  }

  // ──────────────────────────────────────────────────────────────────────────
  // GEO-FENCING
  // ──────────────────────────────────────────────────────────────────────────

  getIpGeofencing(): void {
    this.eSignService.getIpGeofencing({ order_id: this.orderId }).subscribe(
      (res) => {
        this.geoFencingData = res.content;
        if (res.content.allowed_ip === false) {
          this.showGeoFencingModal();
        }
      },
      (error) => {
        console.error('Error fetching geo-fencing data:', error);
        this.isGeoFencingAllowed = true;
      }
    );
  }

  showGeoFencingModal(): void {
    this.modalService
      .open(this.geoFencingModal, {
        centered: true,
        backdrop: 'static',
        keyboard: false,
        windowClass: 'geo-fencing-modal'
      })
      .result.then((result) => {
        if (result === 'close') {
          const apiUrl = environment.apiURL;
          const url = new URL(apiUrl);
          url.hostname = url.hostname.replace('-api', '');
          const finalUrl = url.toString();
          if (
            this.urlValidator.isValidRedirectUrl(finalUrl, DomainType.DOQFY)
          ) {
            window.location.href = finalUrl;
          }
        }
      });
  }

  // ──────────────────────────────────────────────────────────────────────────
  // SEQUENCE ORDER
  // ──────────────────────────────────────────────────────────────────────────

  getSequenceOrder(): void {
    this.beforeSigners = [];
    this.afterSigners = [];
    if (this.otherSignerDetails.length > 0) {
      this.otherSignerDetails.forEach((element) => {
        if (
          element.signatory_sequence >
            this.signatoryDetails.signatory_sequence ||
          element.status !== 'COMPLETED'
        ) {
          this.afterSigners.push(element);
        } else if (element.signatory_sequence === 0) {
          this.zeroSigners.push(element);
        } else if (
          element.signatory_sequence === 0 &&
          element.status === 'COMPLETED'
        ) {
          this.beforeSigners.push(element);
        }
        if (element.status === 'COMPLETED') {
          this.completedSigners.push(element);
        }
      });
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // PDF
  // ──────────────────────────────────────────────────────────────────────────

  // numPages and page sizes are read via pdf.js's public API (event.numPages,
  // event.getPage()) directly off the after-load-complete event — no need to
  // poll pdf.js's private _transport.pageCache internals until it happens to
  // fill up (which, for a large document that's never fully scrolled through,
  // may never happen at all).
  pdfLoaded(event: any): void {
    if (!this.pageLoaded) return;
    if (this.pageVariable === this.numPdfPages) return;
    this.pageLoaded = false;

    this.numPdfPages = event.numPages;
    this.pageJumpValue = this.pageVariable;

    event.getPage(1).then((page: any) => {
      const viewport = page.getViewport({ scale: 1 });
      this.pdfsizes[0] = { width: viewport.width, height: viewport.height };
      this.pageHeight = this.convertPtToPx(this.pdfsizes[0].height);

      if (this.positionDetails['ALL']) {
        const coordinates: Coordinates[] = this.positionDetails['ALL'];
        const newPositionDetails: PositionDetails = {};
        for (let j = 1; j <= this.numPdfPages; j++) {
          newPositionDetails[j.toString()] = coordinates;
        }
        delete this.positionDetails['ALL'];
        Object.assign(this.positionDetails, newPositionDetails);
      }

      this.gotoPagenumber(this.positionDetails);
      $('.example-boundary').css(
        'width',
        this.convertPtToPx(this.pdfsizes[0].width) + 'px'
      );
      $('.example-boundary').css(
        'height',
        this.convertPtToPx(this.pdfsizes[0].height) + 'px'
      );
      $('.preview-upload').css(
        'width',
        this.convertPtToPx(this.pdfsizes[0].width) + 'px'
      );
      $('.preview-upload').css(
        'height',
        this.convertPtToPx(this.pdfsizes[0].height) + 'px'
      );
    });
  }

  // The signature area is drawn at the size this order will actually be signed
  // at (its own x1..x2 / y1..y2), instead of a fixed 80x30. New orders all use
  // the common signature box.
  getBoxSizePx(item): { width: number; height: number } {
    return getSignatureBoxSizePx(item);
  }

  convertPtToPx(pt: number): number {
    return (4 / 3) * pt;
  }
  convertPxToPt(px: number): number {
    return (4 / 3) * px;
  }

  pageChange(pageNo: number): void {
    this.pageVariable = pageNo;
    this.pageNo = pageNo;
    this.pageJumpValue = pageNo;
  }

  jumpToPage(): void {
    const target = Math.round(this.pageJumpValue);
    if (!this.numPdfPages || isNaN(target)) return;
    const clamped = Math.max(1, Math.min(target, this.numPdfPages));
    this.pageJumpValue = clamped;
    this.pageChange(clamped);
  }

  gotoPagenumber(positionDetails: any): void {
    this.signPages = Object.keys(positionDetails).map(Number);
    this.pageNo = this.signPages[0];
    this.pageChange(this.signPages[0]);
  }

  getPosition(item): string {
    if (!item) return 'translate3d(0px, 0px, 0px)';
    const exampleBoundary = document.querySelector('.example-boundary');
    if (!exampleBoundary) return 'translate3d(0px, 0px, 0px)';
    const boundaryStyles = window.getComputedStyle(exampleBoundary);
    const boundaryHeight = parseFloat(boundaryStyles.height) || 0;
    const translateX = Math.floor(this.convertPtToPx(item.x1));
    const translateY = Math.floor(this.convertPtToPx(item.y1));

    if (this.signPosition === 'DRAG_DROP') {
      const ty = Math.floor(boundaryHeight - this.convertPtToPx(item.y1) - 70);
      return `translate3d(${translateX}px, ${ty}px, 0px)`;
    } else {
      if (this.pageHeight < 1100 && translateY > 1000) {
        return `translate3d(${translateX}px, ${this.pageHeight - 130}px, 0px)`;
      }
      return `translate3d(${translateX}px, ${Math.floor(this.convertPtToPx(item.y1))}px, 0px)`;
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // RESIZE PDF  (CAT_EDIT orders)
  // ──────────────────────────────────────────────────────────────────────────

  async getReSizeDocument(base64String: string): Promise<void> {
    try {
      const pdfDoc = await PDFDocument.load(base64String);
      this.numPdfPages = pdfDoc.getPageCount();
      for (let i = 0; i < pdfDoc.getPageCount(); i++) {
        const page = pdfDoc.getPage(i);
        const canvas = document.createElement('canvas');
        canvas.getContext('2d');
        canvas.width = 595;
        canvas.height = 842;
        const { width, height } = page.getSize();
        await page.drawText('', { x: 0, y: height });
        const pageImage = canvas.toDataURL('image/png');
        const imageBytes = Uint8Array.from(atob(pageImage.split(',')[1]), (c) =>
          c.charCodeAt(0)
        );
        const image = await pdfDoc.embedPng(imageBytes);
        page.drawImage(image, { x: 0, y: height, width, height });

        // Yield to the event loop every few pages so the tab can still paint
        // and respond to input during large (thousands-of-pages) conversions.
        if (i % 5 === 4) {
          await new Promise((resolve) => requestAnimationFrame(resolve));
        }
      }
      const modifiedPdfBytes = await pdfDoc.save();
      this.base64Data = this.arrayBufferToBase64(modifiedPdfBytes);
    } catch (error) {
      console.error('Error loading PDF:', error);
    }
  }

  arrayBufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
    const bytes =
      buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return window.btoa(binary);
  }

  // ──────────────────────────────────────────────────────────────────────────
  // ZOOM
  // ──────────────────────────────────────────────────────────────────────────

  zoomIn(): void {
    if (this.zoomLevel < this.ZOOM_MAX)
      this.zoomLevel = Math.min(this.zoomLevel + this.ZOOM_STEP, this.ZOOM_MAX);
  }
  zoomOut(): void {
    if (this.zoomLevel > this.ZOOM_MIN)
      this.zoomLevel = Math.max(this.zoomLevel - this.ZOOM_STEP, this.ZOOM_MIN);
  }
  resetZoom(): void {
    this.zoomLevel = 100;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // CLIENT DATA / LOCATION
  // ──────────────────────────────────────────────────────────────────────────

  getClientData(): void {
    this.clientDetailsService.getClientDetails().then((res) => {
      this.userData = {
        ip_address: res.ip_address,
        latitude: res.latitude,
        longitude: res.longitude,
        browser: res.device_info.browser,
        device_type: res.device_info.deviceType,
        timeStamp: res.timeStamp,
        location: '--',
        city: '--',
        state: '--',
        country: '--'
      };
      setTimeout(() => {
        this.getLocationDetails(res);
      }, 1000);
    });
  }

  getLocationDetails(res): void {
    this.clientDetailsService
      .getLocationDetails(res.latitude, res.longitude)
      .subscribe({
        next: (loc: any) => {
          if (!loc) return;
          this.userData = {
            ip_address: res.ip_address,
            latitude: res.latitude,
            longitude: res.longitude,
            browser: res.device_info.browser,
            device_type: res.device_info.deviceType,
            timeStamp: res.timeStamp,
            location: loc?.display_name || '--',
            city: loc?.address?.state_district || loc?.address?.city || '--',
            state: loc?.address?.state || '--',
            country: loc?.address?.country || '--'
          };
        },
        error: (err) => {
          console.warn('Location lookup failed (non-critical):', err);
        }
      });
  }

  // ──────────────────────────────────────────────────────────────────────────
  // WEBCAM
  // ──────────────────────────────────────────────────────────────────────────

  openCam(): void {
    this.showWebcam = true;
  }
  closeCam(): void {
    this.showWebcam = false;
    if (this.isMobile()) {
      this.stopAllCameraStreams();
    }
    this.modalService.dismissAll();
  }

  isMobile(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }

  private stopAllCameraStreams(): void {
    if (navigator.mediaDevices?.getUserMedia) {
      navigator.mediaDevices
        .enumerateDevices()
        .then((devices) => {
          devices.forEach((d) => {
            if (d.kind === 'videoinput') {
            }
          });
        })
        .catch((err) => console.log('Device enumeration error:', err));
    }
  }

  loadCam(content): void {
    if (!this.webcamImage) {
      this.openCam();
      this.modalService
        .open(content, {
          size: 'md',
          ariaLabelledBy: 'modal-basic-title',
          backdrop: 'static',
          keyboard: false
        })
        .result.then(
          (result) => {
            console.log(result);
          },
          (reason) => {
            console.log(reason);
          }
        );
    }
  }

  reTakeImage(content): void {
    this.webcamImage = null;
    this.loadCam(content);
  }

  handleImage(webcamImage: any): void {
    if (!webcamImage) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Camera Not Ready',
        detail: 'Please wait for the camera to fully load before capturing.'
      });
      return;
    }
    const base64Data = webcamImage.split(',')[1] || '';
    if ((base64Data.length * 3) / 4 < 10000) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Capture',
        detail: 'Camera was not ready. Please wait and try again.'
      });
      return;
    }
    this.webcamImage = webcamImage;
    const isMobile = this.isMobile();

    if (this.signatoryDetails?.face_live_detect) {
      this.eSignService
        .detectSignatoryFace({
          user_image: this.webcamImage,
          order_id: this.orderId
        })
        .subscribe(
          (response: any) => {
            if (response.content.live) {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Capture image successfully.'
              });
              isMobile
                ? setTimeout(() => {
                    this.closeCam();
                    setTimeout(() => {
                      this.renderer.setStyle(
                        document.body,
                        'overflow-y',
                        'scroll'
                      );
                    }, 800);
                  }, 300)
                : (this.closeCam(),
                  this.renderer.setStyle(
                    document.body,
                    'overflow-y',
                    'scroll'
                  ));
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Face not detected properly. Kindly retake the image.'
              });
            }
          },
          () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'An error occurred while detecting the face.'
            });
          }
        );
    } else {
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Capture image successfully.'
      });
      isMobile
        ? setTimeout(() => {
            this.closeCam();
            setTimeout(() => {
              this.renderer.setStyle(document.body, 'overflow-y', 'scroll');
            }, 600);
          }, 200)
        : setTimeout(() => {
            this.closeCam();
            this.renderer.setStyle(document.body, 'overflow-y', 'scroll');
          }, 100);
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // CONSENT
  // ──────────────────────────────────────────────────────────────────────────

  onCheckboxChange(event: any): void {
    this.isChecked = event.target.checked;
    this.displayConsentModal = false;

    if (this.isChecked) {
      this.renderer.setStyle(document.body, 'overflow-y', 'scroll');
      if (this.activeTab === 'esignature') {
        setTimeout(() => {
          if (this.userImage) {
            this.webcamImage = null;
            this.openCam();
            this.modalService
              .open(this.userImage, {
                size: 'md',
                ariaLabelledBy: 'modal-basic-title',
                backdrop: 'static',
                keyboard: false
              })
              .result.then(
                (result) => console.log(result),
                (reason) => console.log(reason)
              );
          }
        }, 400);
      }
    } else {
      this.renderer.setStyle(document.body, 'overflow-y', 'hidden');
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // OTP
  // ──────────────────────────────────────────────────────────────────────────

  onOtpMethod(): void {
    this.modalService
      .open(this.otpScreen, {
        size: 'lg',
        ariaLabelledBy: 'modal-basic-title',
        backdrop: 'static',
        keyboard: false
      })
      .result.then(
        (result) => {
          console.log(result);
        },
        (reason) => {
          console.log(reason);
        }
      );
  }

  onOtpChange(otp: string): void {
    this.otp = otp;
    this.enableOtp = this.otp.length !== 6;
  }

  startTimer(): void {
    clearInterval(this.interval);
    this.interval = setInterval(() => {
      if (this.timeLeft > 0) this.timeLeft--;
    }, 1000);
  }

  otpOnSubmit(): void {
    this.eSignService
      .verifySendOtp({
        otp: this.otp,
        email: this.selectedUserEmail,
        contact_number: this.signatoryDetails?.contact_number
      })
      .subscribe(() => {
        this.modalService.dismissAll();
        this.messageService.add({
          severity: 'success',
          summary: 'OTP authenticated successfully',
          detail: ''
        });
        this.displayConsentModal = true;
      });
  }

  sendOtpValidate(): void {
    this.eSignService
      .sendOtpToVerify(
        this.selectedUserEmail,
        this.orderId,
        this.signatoryId,
        this.signatoryDetails?.contact_number
      )
      .subscribe(() => {
        const detailMsg = this.signatoryDetails?.is_sms
          ? 'An OTP has been sent to ' +
            this.signatoryDetails?.email +
            (this.signatoryDetails?.contact_number
              ? ' and ' + this.signatoryDetails?.contact_number
              : '')
          : 'An OTP has been sent to ' + this.signatoryDetails?.email;
        this.messageService.add({
          severity: 'success',
          summary: 'OTP Validation',
          detail: detailMsg
        });
        this.startTimer();
      });
  }

  resendOtp(): void {
    this.sendOtpLink = true;
    this.timeLeft = 60;
    this.startTimer();
    this.sendOtpValidate();
    this.showOtpComponent = false;
    this.otp = null;
    setTimeout(() => {
      this.showOtpComponent = true;
    }, 0);
  }

  // ──────────────────────────────────────────────────────────────────────────
  // AI FEATURES
  // ──────────────────────────────────────────────────────────────────────────

  setActiveTab(tab: string): void {
    this.activeTab = tab;
    this.compareDropError = false;
    this.clearSummary();
    if (tab === 'translate' && !this.languagesLoaded) {
      this.fetchLanguages();
    }
    if (tab === 'translate' && this.documentBase64 && !this.translatePdfSrc) {
      this.translatePdfSrc = `data:application/pdf;base64,${this.documentBase64}`;
    }
  }

  private fetchLanguages(): void {
    this.isLoadingLanguages = true;
    this.aiFeaturesService.getLanguages().subscribe({
      next: (res) => {
        this.supportedLanguages = (res.languages || []).map((lang: any) => ({
          label: lang.name,
          value: lang.code
        }));
        this.languagesLoaded = true;
        this.isLoadingLanguages = false;
      },
      error: () => {
        this.isLoadingLanguages = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Failed to Load Languages',
          detail: 'Could not fetch available languages. Please try again.'
        });
      }
    });
  }

  onLanguageChange(): void {
    if (this.translatedHtml) {
      this.translateDocument();
    }
  }

  translateDocument(): void {
    if (!this.selectedLanguage) {
      this.showLanguageError = true;
      return;
    }
    if (!this.documentBase64) {
      this.messageService.add({
        severity: 'error',
        summary: 'Document Not Ready',
        detail: 'The document is not loaded yet. Please wait and try again.'
      });
      return;
    }
    this.isTranslating = true;
    this.translatedHtml = null;
    this.aiFeaturesService
      .translateDocxB64({
        document: this.documentBase64,
        target_language: this.selectedLanguage
      })
      .subscribe({
        next: (response) => {
          this.isTranslating = false;
          const htmlData =
            response.body?.translated_html ?? response.body?.data ?? null;
          if (htmlData) {
            this.translatedHtml = htmlData;
            this.messageService.add({
              severity: 'success',
              summary: 'Translation Complete',
              detail: 'Your document has been translated successfully.'
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Translation Failed',
              detail: 'No translated content received.'
            });
          }
        },
        error: () => {
          this.isTranslating = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Translation Failed',
            detail: 'An error occurred while translating.'
          });
        }
      });
  }

  onFeatureChange(value: string): void {
    if (value === 'summarize') {
      this.triggerSummarize();
    }
  }

  triggerSummarize(): void {
    if (!this.documentBase64) {
      this.messageService.add({
        severity: 'error',
        summary: 'Document Not Ready',
        detail: 'The document is not loaded yet. Please wait and try again.'
      });
      return;
    }
    this.summaryContent = null;
    this.summaryError = null;
    this.isSummarizing = true;
    this.aiFeaturesService.summarizeDocument(this.documentBase64).subscribe({
      next: (response) => {
        this.isSummarizing = false;
        const content =
          response.body?.summary ??
          response.body?.data ??
          response.body?.content ??
          response.body?.result ??
          null;
        if (content) {
          this.summaryContent = content;
          this.messageService.add({
            severity: 'success',
            summary: 'Summary Ready',
            detail: 'Document summarized successfully.'
          });
        } else {
          this.summaryError = 'No summary content received from the server.';
          this.messageService.add({
            severity: 'error',
            summary: 'Summarize Failed',
            detail: 'No summary content received.'
          });
        }
      },
      error: () => {
        this.isSummarizing = false;
        this.summaryError = 'An error occurred while summarizing the document.';
        this.messageService.add({
          severity: 'error',
          summary: 'Summarize Failed',
          detail: 'An error occurred while summarizing.'
        });
      }
    });
  }

  clearSummary(): void {
    this.summaryContent = null;
    this.summaryError = null;
    this.isSummarizing = false;
    this.selectedFeature = null;
  }

  get isSummarizeActive(): boolean {
    return (
      this.selectedFeature === 'summarize' &&
      (this.isSummarizing || !!this.summaryContent || !!this.summaryError)
    );
  }

  get selectedFeatureLabel(): string {
    if (!this.selectedFeature) return 'Features';
    return (
      this.featureOptions.find((f) => f.value === this.selectedFeature)
        ?.label ?? 'Features'
    );
  }

  resetTranslatePdf(): void {
    this.translatedHtml = null;
    this.selectedLanguage = '';
  }

  // ──────────────────────────────────────────────────────────────────────────
  // COMPARE
  // ──────────────────────────────────────────────────────────────────────────

  onFileSelected(event: any): void {
    const file = event.target.files?.[0];
    if (file) {
      this.compareFile = file;
      this.compareDropError = false;
    }
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.compareFile = file;
      this.compareDropError = false;
    }
  }

  compareDocuments(): void {
    if (!this.compareFile) {
      this.compareDropError = true;
      return;
    }
    this.compareDropError = false;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // EMAIL / PRINT / DOWNLOAD
  // ──────────────────────────────────────────────────────────────────────────

  shareToEmail(): void {
    if (this.isEmailButtonDisabled) {
      this.messageService.add({
        severity: 'warn',
        detail: `Please wait ${this.emailCooldownTime} seconds before sending again.`,
        life: 3000
      });
      return;
    }
    this.isEmailButtonDisabled = true;
    this.eSignService
      .sendEmailNotification({
        esign_id: this.queryParams.esign_id,
        signatory_id: this.queryParams.signatory_id,
        notify_type: 'ESIGN_SUCCESS'
      })
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            detail: 'Signed document has been sent to your email successfully.',
            life: 3500
          });
          this.startEmailCooldown();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            detail: 'Failed to send email. Please try again.',
            life: 3500
          });
          this.isEmailButtonDisabled = false;
        }
      });
  }

  startEmailCooldown(): void {
    this.emailCooldownTime = 10;
    this.emailCooldownInterval = setInterval(() => {
      this.emailCooldownTime--;
      if (this.emailCooldownTime <= 0) {
        this.isEmailButtonDisabled = false;
        clearInterval(this.emailCooldownInterval);
      }
    }, 1000);
  }

  printDocument(): void {
    const documentBase64 = this.signerData?.document;
    if (!documentBase64) return;
    try {
      const file = new Blob(
        [
          new Uint8Array(
            atob(documentBase64)
              .split('')
              .map((c) => c.charCodeAt(0))
          )
        ],
        { type: 'application/pdf' }
      );
      const fileURL = URL.createObjectURL(file);
      const iframe = document.createElement('iframe');
      Object.assign(iframe.style, {
        position: 'fixed',
        right: '0',
        bottom: '0',
        width: '0',
        height: '0',
        border: 'none',
        visibility: 'hidden'
      });
      document.body.appendChild(iframe);
      iframe.src = fileURL;
      iframe.onload = () => {
        setTimeout(() => {
          try {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
            const cleanup = () => {
              setTimeout(() => {
                if (document.body.contains(iframe))
                  document.body.removeChild(iframe);
                URL.revokeObjectURL(fileURL);
              }, 100);
            };
            iframe.contentWindow?.addEventListener('afterprint', cleanup);
            setTimeout(cleanup, 60000);
          } catch (e) {
            if (document.body.contains(iframe))
              document.body.removeChild(iframe);
            URL.revokeObjectURL(fileURL);
          }
        }, 500);
      };
    } catch (error) {
      console.error('Error printing document:', error);
    }
  }

  downloadDocument(): void {
    const documentBase64 = this.signerData?.document;
    const esignId = this.signatoryDetails?.esign_id;
    if (!documentBase64) return;
    try {
      const file = new Blob(
        [
          new Uint8Array(
            atob(documentBase64)
              .split('')
              .map((c) => c.charCodeAt(0))
          )
        ],
        { type: 'application/pdf' }
      );
      const fileURL = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = `${esignId ? esignId.replace(/ /g, '_') : `signed_document_${Date.now()}`}.pdf`;
      link.click();
      URL.revokeObjectURL(fileURL);
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  }

  closeModel(): void {
    this.modalService.dismissAll();
  }
  scrollPage(): void {
    this.renderer.setStyle(
      document.querySelector('body'),
      'overflow',
      'scroll'
    );
  }
}
